import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { fmtUSD } from "@/lib/api";
import { NachaUpload } from "./AmlIngest";
import { DecisionBox, ItemList, Pill } from "./AmlShared";

const SEV = { high: "text-red-700 bg-red-50 ring-red-200", medium: "text-amber-700 bg-amber-50 ring-amber-200", info: "text-sky-700 bg-sky-50 ring-sky-200" };

export function AchView({ data, onData, focus }) {
  const [sel, setSel] = useState(data.items.some((i) => i.id === focus) ? focus : data.items[0].id);
  const b = data.items.find((i) => i.id === sel);
  return (
    <div className="space-y-4"><NachaUpload onData={onData} />
    <div className="grid xl:grid-cols-[300px_1fr] gap-5">
      <ItemList items={data.items} sel={sel} setSel={setSel} render={(i) => (<>
        <div className="flex justify-between gap-2"><span className="num text-xs font-semibold text-slate-900">{i.id}</span><Pill d={i.analyst?.decision || i.engine_decision} id={`aml-list-status-${i.id}`} /></div>
        <div className="text-[11px] text-slate-500 mt-1">{i.direction} · {i.sec} · {i.entries.toLocaleString()} entries</div>
        <div className="text-[11px] text-slate-700 truncate">{i.originator}</div>
      </>)} />
      <div className="space-y-4 min-w-0" data-testid="ach-detail">
        <div className="panel p-4">
          <div className="eyebrow mb-3">Payment relationship chain</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {b.roles.map((r, n) => (
              <div key={r.role} className="flex items-center gap-1.5">
                {n > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                <div data-testid={`ach-role-${n}`} className={`rounded-lg px-3 py-2 ring-1 ${r.name === "-" ? "ring-slate-100 bg-slate-50 text-slate-400" : r.name === "Unknown" || r.name === "(blank)" ? "ring-amber-300 bg-amber-50" : "ring-slate-200 bg-white"}`}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">{r.role}</div><div className="text-xs text-slate-900">{r.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="panel p-4">
            <div className="eyebrow mb-2">Return rates vs Nacha thresholds</div>
            {Object.entries(b.thresholds).map(([k, th]) => (
              <div key={k} className="py-1.5 border-t border-slate-100 first:border-0">
                <div className="flex justify-between text-xs"><span className="text-slate-600 capitalize">{k}</span><span className={`num ${b.returns[k] > th ? "text-red-700 font-semibold" : "text-slate-900"}`} data-testid={`ach-return-${k}`}>{(b.returns[k] * 100).toFixed(2)}% <span className="text-slate-400">/ {(th * 100).toFixed(1)}%</span></span></div>
                <div className="h-1.5 rounded bg-slate-100 mt-1 overflow-hidden"><div className={`h-full ${b.returns[k] > th ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(b.returns[k] / th, 1) * 100}%` }} /></div>
              </div>
            ))}
            <div className="flex justify-between text-xs pt-2 border-t border-slate-100 mt-1"><span className="text-slate-600">Declared vs actual monthly</span><span className="num">{b.declared_monthly ? fmtUSD(b.declared_monthly, true) : "Not declared"} / {fmtUSD(b.actual_monthly, true)}</span></div>
          </div>
          <div className="panel p-4" data-testid="ach-flags">
            <div className="flex justify-between items-center mb-2"><span className="eyebrow">Findings</span><Pill d={b.engine_decision} id="ach-engine-decision" /></div>
            {b.flags.map((f) => <div key={f.text} className={`text-[11px] rounded-lg ring-1 px-2.5 py-1.5 mb-1.5 ${SEV[f.severity]}`}>{f.text}</div>)}
            {!b.flags.length && <div className="text-[11px] text-slate-500">No findings. Roles resolved and returns within thresholds.</div>}
          </div>
        </div>
        <DecisionBox key={b.id} kind="ach" item={b} choices={data.choices} onData={onData} />
      </div>
    </div></div>
  );
}

const CELL = { Covered: "bg-emerald-50 text-emerald-700", Partial: "bg-amber-50 text-amber-700", Gap: "bg-red-50 text-red-700", "Not offered": "bg-slate-50 text-slate-400" };

export function Corridors({ data }) {
  return (
    <div className="space-y-4" data-testid="corridor-matrix">
      <div className="flex flex-wrap gap-3">{Object.entries(data.summary).map(([k, v]) => <div key={k} className={`rounded-xl px-4 py-2.5 ${CELL[k]}`}><div className="num text-xl font-semibold" data-testid={`corridor-count-${k.split(" ")[0].toLowerCase()}`}>{v}</div><div className="text-[11px]">{k}</div></div>)}</div>
      <div className="panel overflow-x-auto thin-scroll">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th className="px-4 py-2.5">Country / corridor</th>{data.rails.map((r) => <th key={r} className="px-3 py-2.5">{r}</th>)}<th className="px-3 py-2.5">Rules</th><th className="px-3 py-2.5">Owner</th><th className="px-3 py-2.5">Rollout</th></tr></thead>
          <tbody>{data.rows.map((r) => (
            <tr key={r.country} className="border-t border-slate-100" data-testid={`corridor-row-${r.country.split(" ")[0].toLowerCase()}`}>
              <td className="px-4 py-2 font-medium text-slate-900">{r.country}</td>
              {r.cells.map((c, i) => <td key={i} className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[11px] font-medium ${CELL[c]}`}>{c}</span></td>)}
              <td className="px-3 py-2 num">{r.rules}</td><td className={`px-3 py-2 ${r.owner === "Unassigned" ? "text-red-600" : "text-slate-700"}`}>{r.owner}</td><td className="px-3 py-2 text-slate-600">{r.priority}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
